import type { SubNavigationLink } from '@ctw/shared/components/navigation/sub-navigation-bar';
import { hrefIsActive } from '@ctw/shared/utils/url';
import { useMemo, useState } from 'react';

/**
 * Resolve which sub-navigation link should render as active, highlighting the clicked link
 * OPTIMISTICALLY (immediately, before the route commits) and reconciling with the committed
 * pathname once it catches up.
 *
 * Correctness is derived from the pathname VALUE, never from `navigationLinks` referential
 * identity: `useZapSections` returns a brand-new array on every render (its `useMemo` is keyed on
 * the callback from `useHasFeatureFlag`, which is a fresh closure each render, so the memo never
 * hits), so reconciling on array identity — e.g. a `useEffect` keyed on `navigationLinks` — would
 * re-run every render and clobber the optimistic highlight on the very next one, leaving the
 * highlight stuck on the committed pathname until navigation finishes.
 *
 * The override is cleared by adjusting state during render when the pathname-derived href changes
 * (React's "storing information from previous renders" pattern) rather than in an effect — an
 * effect that only depends on `pathnameActiveHref` without referencing it in its body gets its
 * dependency array stripped to `[]` by Biome's `--fix --unsafe`, which would silently break the
 * reconciliation.
 *
 * Returns the active href and a setter to call when a link is clicked.
 */
export const useActiveSubNavigationHref = (
  navigationLinks: Array<SubNavigationLink>,
  pathname: string,
): readonly [string | undefined, (href: string) => void] => {
  const pathnameActiveHref = useMemo(
    () =>
      navigationLinks.find(
        (link) =>
          hrefIsActive({ pathname, href: link.href }) ||
          link.siblingHrefs?.some((siblingHref) => hrefIsActive({ pathname, href: siblingHref })),
      )?.href,
    [navigationLinks, pathname],
  );

  const [optimisticActiveHref, setOptimisticActiveHref] = useState<string | undefined>(undefined);
  const [reconciledActiveHref, setReconciledActiveHref] = useState(pathnameActiveHref);

  // When the committed route resolves to a new link (nav completes, or back/forward), drop the
  // optimistic override so the highlight follows the pathname again.
  if (pathnameActiveHref !== reconciledActiveHref) {
    setReconciledActiveHref(pathnameActiveHref);
    setOptimisticActiveHref(undefined);
  }

  return [optimisticActiveHref ?? pathnameActiveHref, setOptimisticActiveHref] as const;
};
